import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PDFViewer, type PluginRegistry } from '@embedpdf/react-pdf-viewer';
import { userManager } from '../../auth/userManager';
import DarkLoadingSpinner from '../../components/LoadingSpinner/DarkLoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTE_PATHS } from '../../routes/routePaths';
import { StartNowProxyClient } from '../../services/axiosConfig';
import { getErrorLogCorrelationId } from '../../services/errorLogger';
import {
  useGetGridDocumentDetails,
  useGetOpinionDocumentDetails,
  useGetPageDocumentDetails,
} from '../../services/postHogServices';
import type { FilePermission } from '../../types/common';
import { safeNavigate } from '../../util';
import { isKeycloakLoginEnabled } from '../../util/constants';
import {
  consumePdfViewerBuffer,
  consumePdfViewerSessionState,
  isFreshPdfViewerLoad,
} from '../../util/downloadRedirect';
import {
  applyPdfViewerNoSelectStyles,
  buildPdfViewerDisabledCategories,
  buildPdfViewerPermissionsFromFilePermission,
  cleanupPdfViewerZoomInputGuards,
  getStoragePdfBlob,
  installPdfViewerInvalidDocumentRedirect,
  installPdfViewerKeyboardGuards,
  installPdfViewerScreenshotGuards,
  parsePdfViewerSource,
  setupEmbedPdfViewerUi,
} from '../../util/pdfViewer';
import {
  buildPdfViewerFeatureProperties,
  enrichPdfViewerAnalyticsContext,
  installPdfViewerAnalytics,
  mergePdfViewerAnalyticsContext,
  parsePdfViewerAnalyticsFromSourceHref,
  PDF_VIEWER_FEATURE_EVENT,
  type PdfViewerActionExtras,
  type PdfViewerActionType,
  type PdfViewerAnalyticsContext,
} from '../../util/pdfViewerAnalytics';
import { captureEventInternal, initAnalytics } from '../../util/posthog';
import './PDFViewerPage.css';

const SCREENSHOT_BLOCKED_MESSAGE = 'Print screen / screenshot is not allowed.';

function lockPageScroll(): () => void {
  const { documentElement, body } = document;
  const previousHtmlHeight = documentElement.style.height;
  const previousBodyHeight = body.style.height;
  const previousBodyOverflow = body.style.overflow;

  documentElement.style.height = '100%';
  body.style.height = '100%';
  body.style.overflow = 'hidden';

  return () => {
    documentElement.style.height = previousHtmlHeight;
    body.style.height = previousBodyHeight;
    body.style.overflow = previousBodyOverflow;
  };
}

function getLoadErrorMessage(error: unknown): string {
  const axiosError = error as {
    response?: { data?: { errors?: Array<{ message?: string }> }; status?: number };
    message?: string;
  };

  return (
    axiosError?.response?.data?.errors?.[0]?.message ??
    axiosError?.message ??
    'Failed to load document. Please try again later.'
  );
}

function isDocumentNotFoundError(message: string, error: unknown): boolean {
  const axiosError = error as { response?: { status?: number } };
  const normalized = message.toLowerCase();

  return (
    normalized.includes('document not found') ||
    normalized.includes('not found') ||
    axiosError?.response?.status === 404
  );
}

function isSafeAppPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//');
}

function documentIdWithoutExtension(value: string | undefined): string {
  if (!value) return '';
  const lastDotIndex = value.lastIndexOf('.');
  if (lastDotIndex > 0) {
    return value.slice(0, lastDotIndex);
  }
  return value;
}

function isNumericEntityId(value: string | undefined | null): value is string {
  return !!value && /^\d+$/.test(value);
}

/** Encrypted hop-page ids are not valid document-details parameters. */
function entityIdForDocumentDetails(
  mergedId: string,
  handoffId: string | undefined,
  handoffAlreadyEnriched: boolean,
): string {
  if (handoffAlreadyEnriched && isNumericEntityId(handoffId)) {
    return '';
  }
  return isNumericEntityId(mergedId) ? mergedId : '';
}

export default function PDFViewerPage() {
  const { isAuthenticated: isFullyAuthenticated, isPartiallyAuthenticated, loading: isAuthLoading } = useAuth();
  const isAuthenticated = isFullyAuthenticated || isPartiallyAuthenticated;
  const [searchParameters] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [viewerSource, setViewerSource] = useState<string | null>(null);
  const [remotePdfBuffer, setRemotePdfBuffer] = useState<ArrayBuffer | null>(null);
  const [screenshotBlocked, setScreenshotBlocked] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const didRedirectRef = useRef(false);
  const documentErrorCleanupRef = useRef<(() => void) | null>(null);
  const analyticsCleanupRef = useRef<(() => void) | null>(null);
  const registryRef = useRef<PluginRegistry | null>(null);
  const screenshotGuardResetRef = useRef<(() => void) | null>(null);
  const analyticsContextRef = useRef<PdfViewerAnalyticsContext>({});
  const analyticsInitPromiseRef = useRef<Promise<void> | null>(null);

  const querySourceUrl = searchParameters.get('sourceUrl')?.trim() || '';
  const couParameters = searchParameters.get('cou')?.trim() || '';
  const sourceParameters = searchParameters.get('src')?.trim() || '';
  const pdfUrlParameter = searchParameters.get('pdfUrl');
  const bufKey = searchParameters.get('buf');

  const [bufferHandoff] = useState(() => consumePdfViewerBuffer(bufKey));

  const parsedPdf = useMemo(
    () => parsePdfViewerSource(pdfUrlParameter, location.hash),
    [location.hash, pdfUrlParameter],
  );

  const pdfSource = parsedPdf?.pdfSrc;
  const pageNumber = parsedPdf?.pageNumber ?? 1;
  const locationState = location.state as {
    filePermission?: FilePermission | null;
    fileName?: string;
    analyticsContext?: PdfViewerAnalyticsContext;
  } | null;
  const pdfBuffer = bufferHandoff?.buffer ?? remotePdfBuffer;
  const hasPdfBuffer = !!pdfBuffer && pdfBuffer.byteLength > 0;
  const [sessionState] = useState(() =>
    locationState?.filePermission || bufferHandoff?.filePermission
      ? null
      : consumePdfViewerSessionState(pdfUrlParameter)
  );
  const isFreshLoad = isFreshPdfViewerLoad(pdfUrlParameter);
  const permissions = useMemo(() => {
    const filePermission =
      bufferHandoff?.filePermission ??
      locationState?.filePermission ??
      sessionState?.filePermission ??
      null;
    return buildPdfViewerPermissionsFromFilePermission(filePermission);
  }, [
    bufferHandoff?.filePermission,
    locationState?.filePermission,
    sessionState?.filePermission,
  ]);
  const exportFileName =
    bufferHandoff?.fileName?.trim() ||
    locationState?.fileName?.trim() ||
    searchParameters.get('fileName')?.trim() ||
    sessionState?.fileName?.trim() ||
    'document.pdf';
  const disabledCategories = useMemo(
    () => buildPdfViewerDisabledCategories(permissions),
    [permissions],
  );
  const viewerRemountKey = useMemo(
    () =>
      [
        hasPdfBuffer ? `buffer:${pdfBuffer.byteLength}` : (viewerSource ?? pdfSource ?? ''),
        permissions.showSearch,
        permissions.showDownload,
        permissions.showPrint,
        permissions.showCopy,
      ].join('|'),
    [permissions, hasPdfBuffer, pdfBuffer, pdfSource, viewerSource],
  );
  const hasDocument = !!(viewerSource || hasPdfBuffer);

  const baseAnalyticsContext = useMemo(() => {
    const fromHandoff =
      bufferHandoff?.analyticsContext ??
      locationState?.analyticsContext ??
      sessionState?.analyticsContext ??
      null;
    const fromSource = parsePdfViewerAnalyticsFromSourceHref(sourceParameters);
    const withFileName: PdfViewerAnalyticsContext = {
      filename: exportFileName,
      fileExtn: exportFileName.includes('.')
        ? exportFileName.slice(exportFileName.lastIndexOf('.') + 1).toLowerCase()
        : 'pdf',
      documentId: documentIdWithoutExtension(
        fromHandoff?.documentId || fromSource?.documentId || exportFileName,
      ),
    };
    return mergePdfViewerAnalyticsContext(
      mergePdfViewerAnalyticsContext(fromHandoff, fromSource),
      withFileName,
    );
  }, [
    bufferHandoff?.analyticsContext,
    exportFileName,
    locationState?.analyticsContext,
    sessionState?.analyticsContext,
    sourceParameters,
  ]);

  const handoffAnalytics =
    bufferHandoff?.analyticsContext ??
    locationState?.analyticsContext ??
    sessionState?.analyticsContext ??
    null;
  const documentIdForApi = baseAnalyticsContext.documentId ?? '';
  const pageIdForApi = entityIdForDocumentDetails(
    baseAnalyticsContext.pageId ?? '',
    handoffAnalytics?.pageId,
    Boolean(handoffAnalytics?.pageName || handoffAnalytics?.productName),
  );
  const opinionIdForApi = entityIdForDocumentDetails(
    baseAnalyticsContext.opinionID ?? '',
    handoffAnalytics?.opinionID,
    Boolean(handoffAnalytics?.opinionName || handoffAnalytics?.productName),
  );
  const gridIdForApi = entityIdForDocumentDetails(
    baseAnalyticsContext.gridID ?? '',
    handoffAnalytics?.gridID,
    Boolean(handoffAnalytics?.gridName || handoffAnalytics?.productName),
  );

  const { data: pageDocumentData } = useGetPageDocumentDetails(pageIdForApi, documentIdForApi);
  const { data: opinionDocumentData } = useGetOpinionDocumentDetails(
    opinionIdForApi,
    documentIdForApi,
  );
  const { data: gridDocumentData } = useGetGridDocumentDetails(gridIdForApi, documentIdForApi);

  const analyticsContext = useMemo(
    () =>
      enrichPdfViewerAnalyticsContext(baseAnalyticsContext, {
        pageDocumentData,
        opinionDocumentData,
        gridDocumentData,
      }),
    [baseAnalyticsContext, gridDocumentData, opinionDocumentData, pageDocumentData],
  );

  analyticsContextRef.current = analyticsContext;

  const redirectToStandardError = useCallback(
    (message: string, isDocumentNotFound: boolean, correlationId?: string) => {
      if (didRedirectRef.current) return;
      didRedirectRef.current = true;
      safeNavigate(() =>
        navigate(
          `${ROUTE_PATHS.STANDARD_ERROR}${isDocumentNotFound ? '?isDocumentError=true' : ''}`,
          {
            state: {
              content: isDocumentNotFound ? 'Document Not Found.' : message,
              isDocumentError: isDocumentNotFound,
              // Same Ref. as the already-logged API failure (its X-Request-ID).
              correlationId,
            },
          },
        ),
      );
    },
    [navigate],
  );

  useEffect(() => {
    if (isAuthLoading || isAuthenticated) return;
    localStorage.setItem('pendingRedirectUrl', `${location.pathname}${location.search}`);
    if (isKeycloakLoginEnabled()) {
      void userManager.signinRedirect();
      return;
    }
    safeNavigate(navigate, ROUTE_PATHS.LOGIN);
  }, [isAuthLoading, isAuthenticated, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    analyticsInitPromiseRef.current = initAnalytics();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const unlockScroll = lockPageScroll();
    const keyboardCleanup = installPdfViewerKeyboardGuards();
    const root = pageRef.current;
    const screenshot =
      root && !permissions.showCopy
        ? installPdfViewerScreenshotGuards(root, () => setScreenshotBlocked(true))
        : null;
    screenshotGuardResetRef.current = screenshot?.reset ?? null;
    if (permissions.showCopy) {
      setScreenshotBlocked(false);
    }

    return () => {
      unlockScroll();
      keyboardCleanup();
      screenshotGuardResetRef.current = null;
      screenshot?.cleanup();
      documentErrorCleanupRef.current?.();
      analyticsCleanupRef.current?.();
      cleanupPdfViewerZoomInputGuards();
    };
  }, [isAuthenticated, permissions.showCopy]);

  useEffect(() => {
    if (!isAuthenticated || hasPdfBuffer) return;

    let cancelled = false;
    let objectUrl: string | undefined;

    const fail = (errorOrMessage: unknown, isDocumentNotFound?: boolean) => {
      const message =
        typeof errorOrMessage === 'string' ? errorOrMessage : getLoadErrorMessage(errorOrMessage);
      redirectToStandardError(
        message,
        isDocumentNotFound ?? isDocumentNotFoundError(message, errorOrMessage),
        getErrorLogCorrelationId(errorOrMessage),
      );
    };

    const loadDocument = async () => {
      if (querySourceUrl) {
        try {
          const response = await StartNowProxyClient.get<Blob>(querySourceUrl, { responseType: 'blob' });
          if (cancelled) return;
          setRemotePdfBuffer(await response.data.arrayBuffer());
        } catch (error) {
          if (!cancelled) fail(error);
        }
        return;
      }

      if (!isFreshLoad && (sourceParameters || pdfSource)) {
        if (!isSafeAppPath(sourceParameters)) {
          fail('Document Not Found.', true);
          return;
        }
        safeNavigate(() => navigate(sourceParameters, { replace: true }));
        return;
      }

      if (!pdfSource) {
        if (couParameters) {
          safeNavigate(() =>
            navigate(`${ROUTE_PATHS.CONDITION_OF_USE_DOC_DOWNLOAD}?${couParameters}`, { replace: true }),
          );
          return;
        }
        fail('Document Not Found.', true);
        return;
      }

      setViewerSource(null);
      try {
        const blob = await getStoragePdfBlob(pdfSource);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setViewerSource(objectUrl);
      } catch (error) {
        if (!cancelled) fail(error);
      }
    };

    void loadDocument();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    couParameters,
    hasPdfBuffer,
    isAuthenticated,
    isFreshLoad,
    navigate,
    pdfSource,
    querySourceUrl,
    redirectToStandardError,
    sourceParameters,
  ]);

  useEffect(() => {
    const root = pageRef.current;
    if (!root || !hasDocument) return;
    return applyPdfViewerNoSelectStyles(root, permissions.showCopy);
  }, [hasDocument, permissions.showCopy]);

  const capturePdfViewerFeature = useCallback((
    actionType: PdfViewerActionType,
    extras?: PdfViewerActionExtras,
  ) => {
    void (async () => {
      if (analyticsInitPromiseRef.current) {
        await analyticsInitPromiseRef.current;
      } else {
        analyticsInitPromiseRef.current = initAnalytics();
        await analyticsInitPromiseRef.current;
      }
      await captureEventInternal(
        PDF_VIEWER_FEATURE_EVENT,
        buildPdfViewerFeatureProperties(analyticsContextRef.current, actionType, extras),
      );
    })();
  }, []);

  const handleReady = useCallback(
    (registry: PluginRegistry) => {
      documentErrorCleanupRef.current?.();
      analyticsCleanupRef.current?.();
      registryRef.current = registry;
      setupEmbedPdfViewerUi(registry, pageNumber, permissions);
      documentErrorCleanupRef.current = installPdfViewerInvalidDocumentRedirect(registry, () => {
        window.close();
      });
      analyticsCleanupRef.current = installPdfViewerAnalytics(registry, capturePdfViewerFeature);
    },
    [capturePdfViewerFeature, pageNumber, permissions],
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!permissions.showCopy) {
        event.preventDefault();
      }
    },
    [permissions.showCopy],
  );

  const handleResumeAfterScreenshotBlock = useCallback(() => {
    screenshotGuardResetRef.current?.();
    setScreenshotBlocked(false);
  }, []);

  if (isAuthLoading || !isAuthenticated) {
    return <DarkLoadingSpinner />;
  }

  return (
    <div ref={pageRef} className="pdf-viewer-page" onContextMenu={handleContextMenu}>
      <div className="pdf-viewer-content">
        {hasDocument ? (
          <PDFViewer
            key={viewerRemountKey}
            style={{ height: '100%', width: '100%' }}
            onReady={handleReady}
            config={{
              documentManager: {
                initialDocuments: [
                  hasPdfBuffer && pdfBuffer
                    ? { buffer: pdfBuffer, name: exportFileName }
                    : { url: viewerSource!, name: exportFileName },
                ],
              },
              theme: { preference: 'light' },
              pan: { defaultMode: 'mobile' },
              // Keep more pages/tiles warm so large ToC/search jumps show content sooner.
              scroll: { defaultBufferSize: 12 },
              tiling: { extraRings: 1 },
              disabledCategories,
              permissions: {
                enforceDocumentPermissions: true,
                overrides: {
                  print: permissions.showPrint,
                  copyContents: permissions.showCopy,
                  extractForAccessibility: false,
                  // Keep PDF link annotations locked so Contents/TOC clicks navigate (not select).
                  modifyAnnotations: false,
                },
              },
            }}
          />
        ) : (
          <DarkLoadingSpinner />
        )}
      </div>
      <div className="pdf-viewer-screenshot-block" role="alert" aria-hidden={!screenshotBlocked}>
        <p>{SCREENSHOT_BLOCKED_MESSAGE}</p>
        <button className="btn btn-primary" type="button" onClick={handleResumeAfterScreenshotBlock}>
          Continue Viewing
        </button>
      </div>
    </div>
  );
}